import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert, Box, Button, CircularProgress, Grid, Paper, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import config from "../../config";
import Header from "../../components/Header";
import { getAuthToken } from "../../utils/authStorage";
import { apiError } from "./attendance/zkStatusStyles";
import { STATUS_CHIP_STYLES, backButtonSx, getFilterToggleSx } from "./hpcAdminStatusStyles";
import { HPC_COLORS } from "./hpcTheme";

const RENEWAL_WINDOWS = [7, 14, 30];
const DEFAULT_RENEWAL_WINDOW = 14;
const EXPIRED_COLOR = "#ff6b6b";

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Order and wording of the cards. Values always come from the API response.
const CARDS = [
  {
    key: "total_members",
    label: "Total Registrations",
    hint: () => "All registrations that are not deleted (provisional definition)",
  },
  {
    key: "active_members",
    label: "Active Members",
    color: STATUS_CHIP_STYLES.active.border,
    hint: () => "Within the effective expiry and not frozen (includes unknown expiry)",
  },
  {
    key: "frozen_members",
    label: "Frozen Members",
    color: STATUS_CHIP_STYLES.frozen.border,
    hint: () => "A credited membership hold covers today",
  },
  {
    key: "expired_members",
    label: "Expired Members",
    color: EXPIRED_COLOR,
    hint: () => "Past the effective expiry (stored expiry + credited frozen days)",
  },
  {
    key: "pending_registrations",
    label: "Pending Registrations",
    color: STATUS_CHIP_STYLES.pending.border,
    hint: () => "Awaiting payment or activation",
  },
  {
    key: "under_review_registrations",
    label: "Under Review",
    color: STATUS_CHIP_STYLES.under_review.border,
    hint: () => "Awaiting medical clearance",
  },
  {
    key: "renewals_due",
    label: "Renewals Due",
    color: HPC_COLORS.hpuOrange,
    hint: (metric) =>
      metric?.window_days
        ? `Effective expiry within ${metric.window_days} days, including today (frozen excluded)`
        : "Expiring soon",
  },
  {
    key: "grace_period_members",
    label: "Grace Period",
    color: STATUS_CHIP_STYLES.grace_period.border,
    hint: () => "Past the effective expiry, grace set by an admin (not eligible)",
  },
  {
    key: "suspended_members",
    label: "Suspended",
    color: STATUS_CHIP_STYLES.suspended.border,
    hint: () => "Suspended by an admin",
  },
  {
    key: "cancelled_members",
    label: "Cancelled",
    hint: () => "Cancelled by an admin",
  },
  {
    key: "inactive_members",
    label: "Inactive",
    hint: () => "Marked inactive by an admin",
  },
  {
    key: "blacklisted_members",
    label: "Blacklisted",
    color: STATUS_CHIP_STYLES.blacklisted.border,
    hint: () => "Blacklisted by an admin",
  },
  {
    key: "founding_members",
    source: "categories",
    label: "Founding Members",
    hint: () => "Active members on a founding package",
  },
  {
    key: "athlete_members",
    source: "categories",
    label: "Athletes",
    hint: () => "Active members marked Active - Athlete",
  },
  {
    key: "corporate_members",
    source: "categories",
    label: "Corporate Members",
    hint: () => "Active members marked Active - Corporate",
  },
  {
    key: "staff_members",
    source: "categories",
    label: "Staff Members",
    hint: () => "Active members marked Staff Member",
  },
  {
    key: "complimentary_members",
    source: "categories",
    label: "Complimentary",
    hint: () => "Active members marked Complimentary",
  },
  {
    key: "other_status",
    source: "reconciliation",
    label: "Other Statuses",
    hint: () => "Registrations with any other stored status; all status cards add up to the total",
  },
  {
    key: "active_expiry_unknown",
    label: "Active, Expiry Unknown",
    color: HPC_COLORS.hpuOrange,
    hint: () => "Active members with no expiry date recorded",
  },
  {
    key: "new_members_this_month",
    label: "New Members This Month",
    hint: () => "",
  },
];

const UNAVAILABLE_REASONS = {
  definition_pending_approval: "Definition pending approval",
  status_data_not_loaded: "Status data not loaded",
};

// A metric is only shown as a number when the API marks it available and sends a number.
const isAvailable = (metric) =>
  Boolean(metric) && metric.available === true && typeof metric.value === "number";

function MetricCard({ label, metric, color, hint }) {
  const available = isAvailable(metric);
  const reason = metric?.reason ? UNAVAILABLE_REASONS[metric.reason] || "Not available" : "Not available";
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="body2" sx={{ opacity: 0.75 }}>{label}</Typography>
      {available ? (
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>{metric.value.toLocaleString()}</Typography>
      ) : (
        <Typography variant="h4" sx={{ fontWeight: 700, opacity: 0.5 }} aria-label="Not available">—</Typography>
      )}
      <Typography variant="caption" sx={{ color: HPC_COLORS.silver, display: "block", mt: 0.5 }}>
        {available ? hint : `Not available yet — ${reason.toLowerCase()}`}
      </Typography>
    </Paper>
  );
}

const errorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401) return "Your session has expired. Please log in again.";
  if (status === 403) return "You do not have access to the HPU dashboard.";
  return apiError(error, "Could not load the HPU dashboard. Please try again.");
};

export default function HpcDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [windowDays, setWindowDays] = useState(DEFAULT_RENEWAL_WINDOW);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const latestLoad = useRef(0);

  const load = useCallback(async (days) => {
    const requestId = ++latestLoad.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/hpc/admin/dashboard`, {
        params: { renewal_window_days: days },
        headers: authHeaders(),
      });
      if (requestId !== latestLoad.current) return;
      setData(res.data);
    } catch (e) {
      if (requestId !== latestLoad.current) return;
      setData(null);
      setError({ message: errorMessage(e), sessionExpired: e?.response?.status === 401 });
    } finally {
      if (requestId === latestLoad.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(windowDays); }, [load, windowDays]);

  const metrics = data?.metrics || {};
  const shownWindow = metrics.renewals_due?.window_days ?? windowDays;
  const isEmpty = data && metrics.total_members?.available && metrics.total_members.value === 0;

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="HPU DASHBOARD" subtitle="Membership overview for TEBA HPU" />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: HPC_COLORS.silver }}>Renewal window:</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={windowDays}
          onChange={(_event, value) => { if (value !== null) setWindowDays(value); }}
          aria-label="Renewal window in days"
          sx={{ gap: 1, "& .MuiToggleButtonGroup-grouped": { borderRadius: "999px !important", px: 1.75, textTransform: "none" } }}
        >
          {RENEWAL_WINDOWS.map((days) => (
            <ToggleButton key={days} value={days} disabled={loading} sx={getFilterToggleSx(HPC_COLORS.hpuOrange)}>
              {days} days
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          variant="outlined" size="small" sx={backButtonSx}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshOutlinedIcon />}
          onClick={() => load(windowDays)} disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {data?.as_of_date && (
        <Typography variant="body2" sx={{ color: HPC_COLORS.silver, mb: 2 }}>
          As of {data.as_of_date}{data.timezone ? ` (${data.timezone})` : ""} · Renewal window: {shownWindow} days
        </Typography>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={error.sessionExpired ? (
            <Button color="inherit" size="small" onClick={() => navigate("/login", { state: { from: location.pathname } })}>
              Log in
            </Button>
          ) : (
            <Button color="inherit" size="small" onClick={() => load(windowDays)} disabled={loading}>
              Retry
            </Button>
          )}
        >
          {error.message}
        </Alert>
      )}

      {loading && !data && !error && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isEmpty && (
        <Alert severity="info" sx={{ mb: 2 }}>No HPU registrations found yet.</Alert>
      )}

      {data && (
        <Grid container spacing={2} sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
          {CARDS.map((card) => {
            const metric = card.source === "reconciliation"
              ? { value: data.reconciliation?.[card.key], available: typeof data.reconciliation?.[card.key] === "number" }
              : card.source === "categories"
                ? data.categories?.[card.key]
                : metrics[card.key];
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={card.key}>
                <MetricCard label={card.label} metric={metric} color={card.color} hint={card.hint(metric)} />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
